import React, {useEffect, useState} from 'react';

/**
 * Tracks the width and height of a container element using ResizeObserver.
 *
 * Masonic's built-in `useContainerPosition` only returns `width` (via
 * `offsetWidth`) and `offset` (distance from document top). It does not
 * return `height`, which `useMasonry` requires to determine how many items
 * to render in the viewport. It also uses a one-shot passive layout effect
 * rather than ResizeObserver, so it doesn't react to dynamic container
 * resizes.
 *
 * This hook fills both gaps: it provides width AND height, and continuously
 * tracks changes via ResizeObserver. It also reads the initial size
 * synchronously via `getBoundingClientRect` so the positioner starts with
 * the correct dimensions on mount, preventing a 0→real reflow that would
 * cause item re-arrangement on back-navigation.
 */
function useContainerSize(scrollRef: React.RefObject<HTMLElement>) {
  const [size, setSize] = useState({width: 0, height: 0});

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) {
      return undefined;
    }

    const {width, height} = el.getBoundingClientRect();
    setSize({width, height});

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setSize({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });

    observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return size;
}

export default useContainerSize;
